/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Aircraft Role family (operator-cop-doctrine §3B.8
 *  "Response resources" marker family; §3A.3 ambient register).
 *
 *  Single source of the colour + label treatment for an aircraft's operational
 *  role. Consumed by the live-aircraft markers (aircraftStyles.ts), the aircraft
 *  side-panel role badge (AircraftMedia), and the Live-Aircraft layer legend /
 *  role sub-toggles (LayerToggle). One role → one hue, always paired with the
 *  role label (§3A.3: never encode meaning by hue alone — a 24/7 room can't have
 *  one operator who can't tell airlift from a water bomber).
 *
 *  Palette (locked law — do NOT re-tune these hex values here. Colour Law 5
 *  retuned water_bomber off the saturated red the urgent anomaly owns and police
 *  off LIVE-GREEN; SYMBOLS may be revised later per the founder):
 *    water_bomber   salmon #f59f9a   (fire suppression — the tip of the spear)
 *    sar            orange #f97316   (search & rescue)
 *    air_ambulance  blue   #3b82f6   (medevac)
 *    police         green  #15803d   (law enforcement — deep, not LIVE-GREEN)
 *    isr            yellow #eab308   (surveillance / recce)
 *    multi_mission  grey   #94a3b8   (mixed / unspecified)
 *    — R.1e roster roles (previously the grey fallback) —
 *    ccg_helo       teal   #2dd4bf   (Coast Guard helicopter)
 *    lentus_airlift violet #a78bfa   (Op LENTUS shuttle — Griffon)
 *    sar_airlift    indigo #818cf8   (strategic airlift — Globemaster)
 *    heavy_lift     pink   #f472b6   (heavy airlift — Chinook)
 *
 *  This family is its OWN encoding domain (§3A.7): coincidental hue reuse with
 *  the hazard palettes (blue, amber, red) is not the blue/amber debt resolved
 *  there — it is a separate marker register. The lock keeps it self-contained:
 *  this module owns its hex constants and must NOT import another family's
 *  tokens, and colour is never load-bearing alone — every marker / badge / swatch
 *  pairs the colour with the role label.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots / ./lifecycleStates / ./sectorStyles /
 *     ./outageStyles (enforced by the no-restricted-imports lint rule in
 *     eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

export interface AircraftRoleStyle {
  /** CSS hex for the marker fill + the side-panel / legend swatch. */
  color: string;
  /** Human label for the role badge (Title Case). */
  label: string;
}

/** The locked palette — one treatment per aircraft_role. */
const AIRCRAFT_ROLE_STYLES: Record<string, AircraftRoleStyle> = {
  water_bomber: { color: '#f59f9a', label: 'Water Bomber' },
  sar: { color: '#f97316', label: 'SAR' },
  air_ambulance: { color: '#3b82f6', label: 'Air Ambulance' },
  police: { color: '#15803d', label: 'Police' },
  isr: { color: '#eab308', label: 'ISR' },
  multi_mission: { color: '#94a3b8', label: 'Multi-mission' },
  // R.1e roster roles — given doctrinal hues (was the grey fallback).
  ccg_helo: { color: '#2dd4bf', label: 'Coast Guard Helicopter' },
  lentus_airlift: { color: '#a78bfa', label: 'LENTUS Airlift' },
  sar_airlift: { color: '#818cf8', label: 'Strategic Airlift' },
  heavy_lift: { color: '#f472b6', label: 'Heavy Lift' },
};

/** Neutral grey for an unknown / unmapped role (never dropped). */
const UNKNOWN_ROLE: AircraftRoleStyle = { color: '#94a3b8', label: 'Aircraft' };

/**
 * Resolve the treatment for an aircraft_role. Tolerant of null / unknown: a null
 * role resolves to the neutral grey 'Aircraft'; an unmapped non-empty string
 * keeps the grey colour but renders its own de-snaked text so a new server role
 * still reads.
 */
export function aircraftRoleStyle(role: string | null | undefined): AircraftRoleStyle {
  if (role && role in AIRCRAFT_ROLE_STYLES) return AIRCRAFT_ROLE_STYLES[role]!;
  if (typeof role === 'string' && role.length > 0) {
    return { color: UNKNOWN_ROLE.color, label: role.replace(/_/g, ' ') };
  }
  return UNKNOWN_ROLE;
}
